import { Dispatch, SetStateAction, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router-dom'
import { toast } from '@node-space/ui-components'
import { UKRAINE } from 'constants/contacts'
import { PathNames } from 'constants/General'
import { dateInitialErrors, useDateInput } from 'hooks/forms/useDateInput'
import { useFormFields } from 'hooks/forms/useFormFields'
import { usePostContactMutation } from 'hooks/mutations/usePostContact'
import { usePutContactMutation } from 'hooks/mutations/usePutContactMutation'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  ContactEntity,
  ContactInstrumentDecisionStep,
  ContactPayload,
  PutContactProps,
  RelationshipType,
} from 'types/contacts'
import { DateErrorType } from 'types/singleDate'
import { ActionIds } from 'types/types'
import {
  contactAtom,
  contactInstrumentDecisionStepAtom,
  editingContactAtom,
} from '../atoms/contactAtoms'
import { ContactFormData, ContactFormKeys, contactSchema } from '../schemas/contactSchema'
import { getContactDefaultValues, handleContactOnError } from '../utils/utils'

interface UseContactFormProps {
  setDateErrors: Dispatch<SetStateAction<DateErrorType>>
}

export const useContactForm = ({ setDateErrors }: UseContactFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const match = useMatch(PathNames.CRYPTO_CONTACTS)
  const setContactInstrumentDecisionStep = useSetAtom(contactInstrumentDecisionStepAtom)
  const queryClient = useQueryClient()
  const { formatDateInput, formatDateData } = useDateInput()
  const [contact, setContact] = useAtom(contactAtom)
  const setEditingContact = useSetAtom(editingContactAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: postContact, isPending: isPostingContact } = usePostContactMutation()
  const { mutate: updateContact, isPending: isUpdatingContact } = usePutContactMutation()
  const { data: countries, isPending: isCountriesLoading } = useCountriesQuery(true)
  const [action, setAction] = useAtom(actionAtom)

  const countriesOptions = countries?.map(country => ({
    leftText: country?.name,
    value: country?.code,
  }))

  const formSchema = contactSchema(t)

  const isEditingContact = !isEmpty(contact)

  useEffect(() => {
    setEditingContact(isEditingContact)
  }, [isEditingContact])

  const form = useForm<ContactFormData>({
    defaultValues: getContactDefaultValues(isEditingContact, contact, formatDateData),
    resolver: yupResolver(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const isCompany = formValues?.entityType === ContactEntity.COMPANY
  const isIndividual = formValues?.entityType === ContactEntity.INDIVIDUAL

  const {
    setRadioField,
    setDatePickerField,
    setInputField,
    setTravelRuleCheckboxField,
    setComboboxField,
  } = useFormFields<ContactFormKeys>(formSchema, form, formValues)

  const formFields: { [key in ContactFormKeys] } = {
    entityType: setRadioField({
      key: 'entityType',
      options: [
        { value: ContactEntity.COMPANY, label: t('contacts.company') },
        { value: ContactEntity.INDIVIDUAL, label: t('contacts.individual') },
      ],
      onChangeAdditional: () => {
        setDateErrors(dateInitialErrors)
        setValue('dateOfBirth', '')
        clearErrors()
        setRequestError({
          show: false,
        })
      },
    }),
    companyName: setInputField({
      key: 'companyName',
      placeholder: t('formFields.placeholder.companyName'),
    }),
    contactDescription: setInputField({
      key: 'contactDescription',
      placeholder: isCompany
        ? t('formFields.placeholder.contactCompanyDescription')
        : t('formFields.placeholder.contactPersonDescription'),
    }),
    companyRegistrationNumber: setInputField({
      key: 'companyRegistrationNumber',
    }),
    companyRegistrationCountry: setComboboxField({
      key: 'companyRegistrationCountry',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('companyRegistrationCountry'),
    }),
    addressLine1: setInputField({
      key: 'addressLine1',
    }),
    addressLine2: setInputField({
      key: 'addressLine2',
    }),
    city: setInputField({
      key: 'city',
    }),
    postCode: setInputField({
      key: 'postCode',
    }),
    region: setInputField({
      key: 'region',
    }),
    firstName: setInputField({
      key: 'firstName',
      placeholder: t('formFields.placeholder.firstNameAndMiddleNames'),
    }),
    lastName: setInputField({
      key: 'lastName',
    }),
    countryOfResidence: setComboboxField({
      key: 'countryOfResidence',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('countryOfResidence'),
    }),
    isSelfOwned: setTravelRuleCheckboxField({
      key: 'isSelfOwned',
      name: 'isSelfOwned',
      id: 'isSelfOwned',
      testId: 'is-self-owned',
    }),
    dateOfBirth: setDatePickerField({ key: 'dateOfBirth' }),
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const data: ContactPayload = {
      description: formValues?.contactDescription,
      relationshipType: isIndividual
        ? RelationshipType.THIRD_PARTY
        : formValues?.isSelfOwned
          ? RelationshipType.SELF_OWNED
          : RelationshipType.THIRD_PARTY,
      entity: {
        ...(isCompany && {
          legalName: formValues?.companyName,
          registrationNumber: formValues?.companyRegistrationNumber,
        }),
        ...(isIndividual && {
          firstName: formValues?.firstName,
          lastName: formValues?.lastName,
          dateOfBirth: formatDateInput(formValues?.dateOfBirth),
        }),
        address: {
          addressLine1: formValues?.addressLine1,
          addressLine2: formValues?.addressLine2,
          city: formValues?.city,
          postCode: formValues?.postCode,
          ...(formValues?.region && { region: formValues.region }),
          country: isCompany
            ? formValues?.companyRegistrationCountry
            : formValues?.countryOfResidence,
        },
        type: formValues?.entityType,
      },
    }
    if (!isEditingContact) {
      postContact(data, {
        onSuccess: response => {
          toast({
            header: t('contacts.addContactSuccess', {
              contactName: response?.name,
            }),
            variant: 'success',
          })
          setContact(response)
          if (action?.actionId === ActionIds?.CONTACT_INSTRUMENT_DECISION) {
            setContactInstrumentDecisionStep(ContactInstrumentDecisionStep.INSTRUMENT)
          } else if (action?.meta?.enableCreateInstrument) {
            setAction({
              ...action,
              header: t('contacts.addCryptoAccount'),
              actionId: ActionIds.INSTRUMENT,
            })
          } else if (match) {
            navigate(`${PathNames.CRYPTO_CONTACTS}/${response?.id}`)
          } else {
            setContact(null)
            action?.onClose()
            queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
          }
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        },
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePostContactMutation')
        },
      })
    } else {
      const payload: PutContactProps = {
        contact: data,
        contactId: contact?.id,
      }
      updateContact(payload, {
        onSuccess: response => {
          toast({
            header: t('contacts.updateContactSuccess', {
              contactName: response?.name,
            }),
            variant: 'success',
          })
          setContact(null)
          action?.onClose()
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        },
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePutContactMutation')
        },
      })
    }
  }

  const onChangeCountry = (
    key: 'countryOfResidence' | 'companyRegistrationCountry',
    value: string
  ) => {
    setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
    value !== UKRAINE && trigger('region')
  }

  return {
    formFields,
    formValues,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    requestError,
    isEditingContact,
    isUpdatingContact,
    handleSubmit,
    onSubmit,
    setValue,
    onChangeCountry,
  }
}
